// Der Entwurf: das Projekt, wie dieser Browser es zuletzt gesehen hat.
//
// Ein Eintrag je Projekt in IndexedDB, geschrieben von draftSync.ts. Er ist
// der Rücken der Offline-Fähigkeit: ohne Netz kommt der Editor aus ihm, und
// mit Netz wird aus ihm nachgeschickt, was der Server noch nicht hat
// (docs/EDITOR.md, „Offline"). `dirty` sagt, ob das Dokument darin dem
// Server voraus ist; `rasterDirty` dasselbe für die Bitmap.
//
// Der Speicher steht hinter einem kleinen Interface, damit die Logik darüber
// ohne Browser getestet werden kann: `memoryStorage()` für vitest,
// `indexedDbStorage()` für die Seite. Beide versprechen dasselbe -- und beide
// dürfen scheitern, ohne dass der Editor davon etwas merkt: jeder Fehler
// endet als `null` oder als stilles Nichtschreiben, nie als Ausnahme im
// Aufrufer.

import type { EditorDocument } from './document'
import { DATABASE_NAME, DRAFT_STORE, connection, request } from './indexedDb'

// Weitergereicht, damit die Aufrufer von früher (lib/serviceWorker.ts) nicht
// zwei Dateien kennen müssen. Die Wahrheit steht in indexedDb.ts.
export const DRAFT_DATABASE = DATABASE_NAME
export { DRAFT_STORE }

export type Draft = {
  projectId: number
  name: string
  document: EditorDocument
  // Was der Server zuletzt bestätigt hat -- die Vergleichsbasis, mit der der
  // Store einen 409 als Umbenennung oder als Konflikt einordnet.
  savedDocument: EditorDocument
  lockVersion: number
  dirty: boolean
  raster: Blob | null
  rasterDirty: boolean
  updatedAt: number
}

export type DraftStorage = {
  get(projectId: number): Promise<Draft | null>
  put(draft: Draft): Promise<void>
  delete(projectId: number): Promise<void>
  clear(): Promise<void>
  // Welche Projekte hier einen Entwurf haben -- nur die Kennungen, ohne die
  // Blobs zu laden. Das Dashboard markiert damit offline, was lokal
  // weitergeht (docs/superpowers/specs/2026-09-06-pwa-offline-round.md).
  list(): Promise<number[]>
}

export function memoryStorage(): DraftStorage {
  const drafts = new Map<number, Draft>()

  return {
    get: async (projectId) => drafts.get(projectId) ?? null,
    put: async (draft) => {
      drafts.set(draft.projectId, draft)
    },
    delete: async (projectId) => {
      drafts.delete(projectId)
    },
    clear: async () => drafts.clear(),
    list: async () => [...drafts.keys()],
  }
}

export function indexedDbStorage(factory: IDBFactory): DraftStorage {
  const open = connection(factory)

  const run = async <T>(mode: IDBTransactionMode, act: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const db = await open()
    const transaction = db.transaction(DRAFT_STORE, mode)
    return request(act(transaction.objectStore(DRAFT_STORE)))
  }

  return {
    get: (projectId) =>
      run('readonly', (store) => store.get(projectId) as IDBRequest<Draft | undefined>)
        .then((draft) => draft ?? null)
        .catch(() => null),
    put: (draft) => run('readwrite', (store) => store.put(draft)).then(() => undefined).catch(() => undefined),
    delete: (projectId) => run('readwrite', (store) => store.delete(projectId)).catch(() => undefined),
    clear: () => run('readwrite', (store) => store.clear()).catch(() => undefined),
    list: () =>
      run('readonly', (store) => store.getAllKeys() as IDBRequest<IDBValidKey[]>)
        .then((keys) => keys.filter((key): key is number => typeof key === 'number'))
        .catch(() => []),
  }
}

/** Der Speicher dieser Seite, oder null in einem Browser ohne IndexedDB. */
export function defaultDraftStorage(): DraftStorage | null {
  if (typeof indexedDB === 'undefined') return null
  return indexedDbStorage(indexedDB)
}
